import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

declare global {
    interface Window {
        ym?: (id: number, action: string, ...params: unknown[]) => void
    }
}

/** Параметры цели / события (как в доке Яндекс.Метрики). */
export type YandexMetricaGoalParams = Record<string, unknown>
export type YandexMetricaParams = Record<string, unknown>

export const useYandexMetrica = (counterId?: string) => {
    const location = useLocation()
    const id = counterId || __YANDEX_METRICA_ID__

    useEffect(() => {
        if (!id || typeof window === 'undefined') return

        const numericId = parseInt(id, 10)
        if (isNaN(numericId) || !window.ym) return

        window.ym(numericId, 'hit', location.pathname + location.search)
    }, [location, id])

    const reachGoal = useCallback(
        (goalId: string, params?: YandexMetricaGoalParams) => {
            if (!id || typeof window === 'undefined') return

            const numericId = parseInt(id, 10)
            if (isNaN(numericId) || !window.ym) return

            window.ym(numericId, 'reachGoal', goalId, params)
        },
        [id]
    )

    const params = useCallback(
        (parameters: YandexMetricaParams) => {
            if (!id || typeof window === 'undefined') return

            const numericId = parseInt(id, 10)
            if (isNaN(numericId) || !window.ym) return

            window.ym(numericId, 'params', parameters)
        },
        [id]
    )

    const userParams = useCallback(
        (parameters: YandexMetricaParams) => {
            if (!id || typeof window === 'undefined') return

            const numericId = parseInt(id, 10)
            if (isNaN(numericId) || !window.ym) return

            window.ym(numericId, 'userParams', parameters)
        },
        [id]
    )

    return {
        reachGoal,
        params,
        userParams,
        isEnabled: !!id && !!window.ym,
    }
}

export const YandexMetrica = {
    reachGoal: (goalId: string, params?: YandexMetricaGoalParams) => {
        const id = __YANDEX_METRICA_ID__
        if (!id || typeof window === 'undefined') return

        const numericId = parseInt(id, 10)
        if (isNaN(numericId) || !window.ym) return

        window.ym(numericId, 'reachGoal', goalId, params)
    },

    params: (parameters: YandexMetricaParams) => {
        const id = __YANDEX_METRICA_ID__
        if (!id || typeof window === 'undefined') return

        const numericId = parseInt(id, 10)
        if (isNaN(numericId) || !window.ym) return

        window.ym(numericId, 'params', parameters)
    },

    userParams: (parameters: YandexMetricaParams) => {
        const id = __YANDEX_METRICA_ID__
        if (!id || typeof window === 'undefined') return

        const numericId = parseInt(id, 10)
        if (isNaN(numericId) || !window.ym) return

        window.ym(numericId, 'userParams', parameters)
    },

    isAvailable: () => {
        return !!__YANDEX_METRICA_ID__ && typeof window !== 'undefined' && !!window.ym
    },
}
